<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { Cropper } from 'vue-advanced-cropper';

import type { CroppingInfo } from '@/components/ImageField.vue';
import type { SavingOptions } from '@/services/types/file-uploader.types';
import type { FileInfo } from '@/services/types/file.types';

type StencilProps = { aspectRatio: number };

// PROPS
const props = defineProps<{
  cropping: CroppingInfo;
  file?: FileInfo | null;
  onCancel: () => void;
  onSave: (options: SavingOptions) => Promise<void>;
}>();

// REACTIVE
const image = reactive({
  src: props.file?.url ?? '',
  type: props.file?.metadata?.mimetype ?? 'image/jpg',
});

// COMPUTED
const hasImage = computed(() => !!image.src);

// REFS
const cropperRef = ref<InstanceType<typeof Cropper>>();
const files = ref<File[]>([]);
const inputFileRef = ref<HTMLInputElement>();
const isLoading = ref<boolean>(false);
const stencilProps = computed<StencilProps>(() => ({ aspectRatio: props.cropping.aspectRatio }));

// LIFECYCLE
onMounted(() => {
  const coordinates = props.file?.metadata?.image?.coordinates;
  if (coordinates) cropperRef.value?.setCoordinates(coordinates);
});

// FUNCTIONS
async function handleSave(): Promise<void> {
  try {
    isLoading.value = true;
    const result = cropperRef.value?.getResult();
    if (!result?.canvas) return;
    await props.onSave({
      files: files.value,
      metadata: { image: { coordinates: result.coordinates, size: props.cropping.size }, type: 'image' },
      version: 'v2',
    });
  } finally {
    isLoading.value = false;
  }
}

function handleUpload(event: Event): void {
  const target = event.target as unknown as { files: FileList };
  const file = target.files?.[0];
  files.value = file ? [file] : [];
  if (file) {
    if (image.src) URL.revokeObjectURL(image.src);
    image.src = URL.createObjectURL(file);
    image.type = file.type;
  }
}
</script>
<template>
  <div class="base-image-cropper" v-if="hasImage">
    <cropper ref="cropperRef" class="cropper" :resize-image="false" :src="image.src" :stencil-props="stencilProps" />
    <div class="base-image-cropper__buttons">
      <div class="base-image-cropper__buttons--edit">
        <div class="base-image-cropper__buttons--left">
          <q-btn @click="props.onCancel" flat :label="$t('global.cancel')" />
        </div>
        <div class="base-image-cropper__buttons--right">
          <q-btn color="primary" @click="handleSave" :label="$t('global.save')" :loading="isLoading" />
        </div>
      </div>
    </div>
  </div>
  <div class="base-image-cropper__new" v-else>
    <div class="base-image-cropper__new--border" @click="inputFileRef?.click()">
      <div class="base-image-cropper__new--text">{{ $t('upload-image-modal.add-photo') }}</div>
    </div>
    <div class="base-image-cropper__buttons">
      <div class="base-image-cropper__buttons--edit">
        <div class="base-image-cropper__buttons--left">
          <q-btn @click="props.onCancel" flat :label="$t('global.cancel')" />
        </div>
      </div>
    </div>
    <input
      ref="inputFileRef"
      accept="image/*"
      class="base-image-cropper__new--file-upload"
      type="file"
      @change="handleUpload"
    />
  </div>
</template>
<style lang="scss">
.base-image-cropper {
  width: 750px;
  display: flex;
  flex-direction: column;
  &__new {
    height: 500px;
    width: 750px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    &--border {
      border: 2px #333333 dashed;
      border-radius: 8px;
      width: 100%;
      height: 100%;
      transition: 200ms;
      cursor: pointer;
      &:hover {
        border-color: #0000ff;
      }
    }
    &--text {
      font-size: 14px;
      font-weight: bold;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #333333;
      margin-bottom: 20px;
      transition: 200ms;
      &:hover {
        color: #0000ff;
      }
    }
    &--button {
      margin-top: 8px;
      width: 100%;
      display: flex;
      justify-content: flex-end;
    }
    &--file-upload {
      display: none;
    }
  }
  &__buttons {
    padding-top: 16px;
    display: flex;
    justify-content: space-between;
    &--edit {
      width: 100%;
      display: flex;
      justify-content: space-between;
    }
    &--right {
      display: flex;
    }
    &--ratio {
      display: flex;
      align-items: center;
      font-weight: bold;
      margin-right: 20px;
    }
    &--modify {
      width: 100%;
      display: flex;
      justify-content: space-between;
    }
    &--left {
      .base-button {
        margin-right: 20px;
      }
    }
  }
}
.cropper {
  height: 400px;
  width: 800px;
}
</style>
